import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { COMPANY_FIELDS, TRUCK_FIELDS, TRAILER_FIELDS, DRIVER_FIELDS } from './constants';

// Llaves únicas por tipo de entidad
const UNIQUE_KEYS = {
  companies: 'cuit_rut',
  trucks: 'license_plate',
  trailers: 'license_plate',
  drivers: 'dni'
};

/**
 * Genera una plantilla Excel para carga masiva
 */
export const generateTemplate = (entityType) => {
  const fieldsMap = {
    companies: COMPANY_FIELDS,
    trucks: TRUCK_FIELDS,
    trailers: TRAILER_FIELDS,
    drivers: DRIVER_FIELDS
  };
  
  const fields = fieldsMap[entityType];
  if (!fields) return;
  
  // Crear headers con nombres descriptivos
  const headers = fields.map(f => f.label);
  
  // Crear fila de ejemplo
  const exampleRow = fields.map(f => {
    if (f.type === 'date') return '2025-12-31';
    if (f.type === 'number') return f.key === 'year' ? 2024 : 0;
    if (f.reference === 'company') return 'CUIT_EMPRESA';
    if (f.key === 'email') return 'ejemplo@correo.com';
    return `EJEMPLO_${f.label.toUpperCase()}`;
  });
  
  // Crear nota con campos obligatorios
  const noteRow = fields.map(f => f.required ? '(OBLIGATORIO)' : '(OPCIONAL)');
  
  const data = [headers, noteRow, exampleRow];
  
  const ws = XLSX.utils.aoa_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
  
  // Ajustar anchos de columna
  ws['!cols'] = fields.map(() => ({ wch: 20 }));
  
  const entityNames = {
    companies: 'Empresas',
    trucks: 'Camiones',
    trailers: 'Acoplados',
    drivers: 'Choferes'
  };
  
  XLSX.writeFile(wb, `Plantilla_${entityNames[entityType]}.xlsx`);
  toast.success(`Plantilla de ${entityNames[entityType]} descargada`);
};

/**
 * Parsea un archivo Excel para carga masiva con deduplicación
 */
export const parseExcelFile = (file, entityType) => {
  return new Promise((resolve, reject) => {
    const fieldsMap = {
      companies: COMPANY_FIELDS,
      trucks: TRUCK_FIELDS,
      trailers: TRAILER_FIELDS,
      drivers: DRIVER_FIELDS
    };
    
    const fields = fieldsMap[entityType];
    if (!fields) {
      reject(new Error('Tipo de entidad no válido'));
      return;
    }
    
    const uniqueKey = UNIQUE_KEYS[entityType];
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        // Obtener headers (primera fila)
        const headers = jsonData[0];
        if (!headers || headers.length === 0) {
          reject(new Error('El archivo está vacío o no tiene encabezados'));
          return;
        }
        
        // Mapear headers a keys de campos
        const headerMap = {};
        fields.forEach(field => {
          const headerIndex = headers.findIndex(h => 
            h && h.toString().toLowerCase().trim() === field.label.toLowerCase().trim()
          );
          if (headerIndex !== -1) {
            headerMap[headerIndex] = field.key;
          }
        });
        
        // Validar que todos los campos obligatorios estén presentes
        const missingRequired = fields
          .filter(f => f.required)
          .filter(f => !Object.values(headerMap).includes(f.key))
          .map(f => f.label);
        
        if (missingRequired.length > 0) {
          reject(new Error(`Faltan columnas obligatorias: ${missingRequired.join(', ')}`));
          return;
        }
        
        // Procesar filas de datos (omitir header y fila de notas si existe)
        const records = [];
        const errors = [];
        const seenKeys = new Set(); // Para detectar duplicados en el archivo
        let duplicatesInFile = 0;
        
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          
          // Omitir filas vacías o que parecen notas
          if (!row || row.length === 0 || (row[0] && row[0].toString().includes('OBLIGATORIO'))) {
            continue;
          }
          
          const record = {};
          let hasData = false;
          
          Object.entries(headerMap).forEach(([index, key]) => {
            const value = row[parseInt(index)];
            if (value !== undefined && value !== null && value !== '') {
              hasData = true;
              // Convertir tipos
              const field = fields.find(f => f.key === key);
              if (field?.type === 'number') {
                record[key] = parseInt(value) || 0;
              } else if (field?.type === 'date') {
                // Manejar fechas de Excel
                if (typeof value === 'number') {
                  const date = XLSX.SSF.parse_date_code(value);
                  record[key] = `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
                } else {
                  record[key] = value.toString().trim();
                }
              } else {
                record[key] = value.toString().trim().toUpperCase();
              }
            }
          });
          
          if (hasData) {
            // Sanitizar patentes (eliminar espacios)
            if ((entityType === 'trucks' || entityType === 'trailers') && record.license_plate) {
              record.license_plate = record.license_plate.replace(/\s/g, '');
            }
            
            // Validar campos obligatorios
            const missingFields = fields
              .filter(f => f.required)
              .filter(f => !record[f.key] || record[f.key] === '')
              .map(f => f.label);
            
            if (missingFields.length > 0) {
              errors.push({ row: i + 1, message: `Faltan campos: ${missingFields.join(', ')}` });
            } else {
              // Verificar duplicados DENTRO del archivo
              const keyValue = record[uniqueKey];
              if (keyValue && seenKeys.has(keyValue)) {
                duplicatesInFile++;
                errors.push({ row: i + 1, message: `Duplicado en archivo: ${uniqueKey} = ${keyValue}` });
              } else {
                if (keyValue) seenKeys.add(keyValue);
                records.push(record);
              }
            }
          }
        }
        
        resolve({ 
          records, 
          errors, 
          total: jsonData.length - 1,
          duplicatesInFile,
          uniqueKey
        });
      } catch (error) {
        reject(new Error(`Error al procesar el archivo: ${error.message}`));
      }
    };
    
    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Obtiene la llave única según el tipo de entidad
 */
export const getUniqueKey = (entityType) => UNIQUE_KEYS[entityType];
